import { useCallback, useEffect, useRef } from 'react';

import { TextArea } from '@/lib/base/forms';
import type { SelectSize } from '@/lib/base/forms/select/select-shared';
import { sizes } from '@/lib/base/forms/select/select-shared';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';
import { cx } from '@/utils/cx';

const insetTable: Record<SelectSize, { icon: string; pl: string }> = {
  xs: { icon: 'left-2.5 top-2', pl: 'pl-8.5' },
  sm: { icon: 'left-3 top-2.5', pl: 'pl-10' },
  md: { icon: 'left-3 top-2.5 sm:left-3.5 sm:top-3', pl: 'pl-10 sm:pl-10.5' },
  lg: { icon: 'left-4 top-3 sm:top-3.5', pl: 'pl-11' },
  xl: { icon: 'left-4 top-3.5 sm:top-4.5', pl: 'pl-12' },
};

export interface PropertyInputProps {
  propertyType: PropertyTypeSchema;
  value: string | number | object | null | undefined;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  placeholder?: string;
  /**
   * Field size on the shared library scale (`SelectSize` — the same scale
   * every `lib/base` form control uses). Defaults to 'sm'.
   */
  size?: SelectSize;
}

const MIN_ROWS = 3;
const MAX_ROWS = 10;
const LINE_HEIGHT = 24;

export const TextAreaInput: React.FC<PropertyInputProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  textareaRef: externalRef,
  placeholder,
  size = 'sm',
}) => {
  const Icon = getPropertyTypeIconComponent(propertyType);
  const internalRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = externalRef || internalRef;

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }
    textarea.style.height = 'auto';
    const minHeight = MIN_ROWS * LINE_HEIGHT;
    const maxHeight = MAX_ROWS * LINE_HEIGHT;
    const contentHeight = textarea.scrollHeight;
    const newHeight = Math.max(minHeight, Math.min(maxHeight, contentHeight));
    textarea.style.height = `${newHeight}px`;
  }, [textareaRef]);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  const handleChange = useCallback(
    (newValue: string) => {
      onChange(newValue === '' ? undefined : newValue);
    },
    [onChange]
  );

  return (
    <div className='relative w-full'>
      <Icon
        className={cx(
          'pointer-events-none absolute z-10 text-fg-quaternary',
          insetTable[size].icon,
          sizes[size].icon
        )}
        aria-hidden='true'
      />
      <TextArea
        ref={textareaRef}
        value={(value as string) || ''}
        onChange={handleChange}
        isDisabled={disabled}
        rows={MIN_ROWS}
        resize='none'
        size={size}
        aria-label={propertyType.label}
        placeholder={placeholder}
        textareaClassName={insetTable[size].pl}
      />
    </div>
  );
};
